import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Video, Users, Calendar, Play, Mic, MicOff, VideoOff, PhoneOff, Monitor } from "lucide-react";

const VideoCallPage = () => {
  const { roomId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [token, setToken] = useState(null);
  const [roomUrl, setRoomUrl] = useState(null);
  const callFrameRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchRoom();
  }, [roomId]);

  useEffect(() => {
    return () => {
      if (callFrameRef.current) {
        callFrameRef.current.destroy();
      }
    };
  }, []);

  const fetchRoom = async () => {
    try {
      const response = await axios.get(`${API}/video/rooms/${roomId}`);
      setRoom(response.data);
    } catch (error) {
      toast.error("Video room not found");
      navigate("/video");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCall = async () => {
    setJoining(true);
    try {
      const response = await axios.post(`${API}/video/rooms/${roomId}/token`);
      setToken(response.data.token);
      setRoomUrl(response.data.room_url);

      if (response.data.room_url && response.data.token) {
        // Dynamic import of Daily.co
        const DailyIframe = (await import("@daily-co/daily-js")).default;
        
        const callFrame = DailyIframe.createFrame(containerRef.current, {
          iframeStyle: {
            width: "100%",
            height: "100%",
            border: "0",
            borderRadius: "16px"
          },
          showLeaveButton: true,
          showFullscreenButton: true
        });

        callFrameRef.current = callFrame;

        callFrame.on("left-meeting", () => {
          setInCall(false);
          callFrame.destroy();
          callFrameRef.current = null;
        });

        await callFrame.join({
          url: response.data.room_url,
          token: response.data.token
        });

        setInCall(true);
      } else {
        toast.error("Video calling is not configured. Please add your Daily.co API key.");
      }
    } catch (error) {
      toast.error("Failed to join the call");
      console.error(error);
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveCall = () => {
    if (callFrameRef.current) {
      callFrameRef.current.leave();
    }
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString("en-CA", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#6B4E8A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#6B4E8A]" data-testid="video-call-page">
      {!inCall ? (
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="max-w-lg w-full bg-white rounded-3xl p-8 text-center">
            <Link 
              to="/video" 
              className="inline-flex items-center text-[#64748B] hover:text-[#6B4E8A] mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sessions
            </Link>

            <div className="w-20 h-20 rounded-2xl bg-[#8DB5A0] flex items-center justify-center mx-auto mb-6">
              <Video className="w-10 h-10 text-white" />
            </div>

            <h1 className="text-2xl font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {room?.name}
            </h1>

            <p className="text-[#64748B] mb-6">{room?.description}</p>

            <div className="space-y-3 mb-8 text-left bg-[#FAF7F2] rounded-xl p-4">
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-5 h-5 text-[#8DB5A0]" />
                <span className="text-[#6B4E8A]">{formatDateTime(room?.scheduled_time)}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Users className="w-5 h-5 text-[#8DB5A0]" />
                <span className="text-[#6B4E8A]">Hosted by {room?.host_name}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Video className="w-5 h-5 text-[#8DB5A0]" />
                <span className="text-[#6B4E8A]">Max {room?.max_participants} participants</span>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                onClick={handleJoinCall}
                className="w-full bg-[#10B981] text-white hover:bg-[#059669] rounded-full py-6 text-lg"
                disabled={joining}
                data-testid="join-call-btn"
              >
                {joining ? (
                  "Joining..."
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" />
                    Join Video Call
                  </>
                )}
              </Button>
              <p className="text-xs text-[#64748B]">
                Make sure your camera and microphone are working before joining.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="h-screen flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-[#6B4E8A]/90 backdrop-blur">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#8DB5A0] flex items-center justify-center">
                <Video className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-medium text-white">{room?.name}</h2>
                <p className="text-xs text-white/70">Hosted by {room?.host_name}</p>
              </div>
            </div>
            <Button
              onClick={handleLeaveCall}
              variant="destructive"
              className="bg-[#EF4444] hover:bg-[#DC2626] rounded-full"
              data-testid="leave-call-btn"
            >
              <PhoneOff className="w-4 h-4 mr-2" />
              Leave
            </Button>
          </div>

          {/* Video Container */}
          <div 
            ref={containerRef}
            className="flex-1 bg-[#6B4E8A]"
            style={{ minHeight: "calc(100vh - 80px)" }}
          />
        </div>
      )}
    </div>
  );
};

export default VideoCallPage;
